export function highchartsRegression(H) {
    function _exponential(data) {
        const sum = [0, 0, 0, 0, 0, 0];
        let n = 0;
        const results = [];
        for (let len = data.length; n < len; n++) {
            if (data[n].x !== null) {
                data[n][0] = data[n].x;
                data[n][1] = data[n].y;
            }
            if (data[n][1] !== null) {
                sum[0] += data[n][0];
                sum[1] += data[n][1];
                sum[2] += data[n][0] * data[n][0] * data[n][1];
                sum[3] += data[n][1] * Math.log(data[n][1]);
                sum[4] += data[n][0] * data[n][1] * Math.log(data[n][1]);
                sum[5] += data[n][0] * data[n][1];
            }
        }
        const denominator = sum[1] * sum[2] - sum[5] * sum[5];
        const A = Math.pow(Math.E, (sum[2] * sum[3] - sum[5] * sum[4]) / denominator);
        const B = (sum[1] * sum[4] - sum[5] * sum[3]) / denominator;
        for (let i = 0, len = data.length; i < len; i++) {
            const coordinate = [
                data[i][0],
                A * Math.pow(Math.E, B * data[i][0]),
            ];
            results.push(coordinate);
        }
        results.sort(function (a, b) {
            if (a[0] > b[0]) {
                return 1;
            }
            if (a[0] < b[0]) {
                return -1;
            }
            return 0;
        });
        const string = `y = ${Math.round(A * 100) / 100}e^(${Math.round(B * 100) / 100}x)`;
        return { equation: [A, B], points: results, string };
    }
    function _linear(data) {
        const sum = [0, 0, 0, 0, 0];
        let n = 0;
        const results = [];
        let N = data.length;
        for (; n < data.length; n++) {
            if (data[n].x !== null) {
                data[n][0] = data[n].x;
                data[n][1] = data[n].y;
            }
            if (data[n][1] !== null && !data[n].ignoreInRegression) {
                sum[0] += data[n][0];
                sum[1] += data[n][1];
                sum[2] += data[n][0] * data[n][0];
                sum[3] += data[n][0] * data[n][1];
                sum[4] += data[n][1] * data[n][1];
            }
            else {
                N -= 1;
            }
        }
        const gradient = (N * sum[3] - sum[0] * sum[1]) / (N * sum[2] - sum[0] * sum[0]);
        const intercept = sum[1] / N - (gradient * sum[0]) / N;
        for (let i = 0, len = data.length; i < len; i++) {
            const coorY = data[i][0] * gradient + intercept;
            const coordinate = [data[i][0], coorY];
            results.push(coordinate);
        }
        results.sort(function (a, b) {
            if (a[0] > b[0]) {
                return 1;
            }
            if (a[0] < b[0]) {
                return -1;
            }
            return 0;
        });
        const string = `y = ${Math.round(gradient * 100) / 100}x + ${Math.round(intercept * 100) / 100}`;
        return {
            equation: [gradient, intercept],
            points: results,
            string,
        };
    }
    function _logarithmic(data) {
        const sum = [0, 0, 0, 0];
        let n = 0;
        const results = [];
        const mean = 0;
        for (let len = data.length; n < len; n++) {
            if (data[n].x !== null) {
                data[n][0] = data[n].x;
                data[n][1] = data[n].y;
            }
            if (data[n][1] !== null) {
                sum[0] += Math.log(data[n][0]);
                sum[1] += data[n][1] * Math.log(data[n][0]);
                sum[2] += data[n][1];
                sum[3] += Math.pow(Math.log(data[n][0]), 2);
            }
        }
        const B = (n * sum[1] - sum[2] * sum[0]) / (n * sum[3] - sum[0] * sum[0]);
        const A = (sum[2] - B * sum[0]) / n;
        for (let i = 0, len = data.length; i < len; i++) {
            const coordinate = [data[i][0], A + B * Math.log(data[i][0])];
            results.push(coordinate);
        }
        results.sort(function (a, b) {
            if (a[0] > b[0]) {
                return 1;
            }
            if (a[0] < b[0]) {
                return -1;
            }
            return 0;
        });
        const string = `y = ${Math.round(A * 100) / 100} + ${Math.round(B * 100) / 100} ln(x)`;
        return { equation: [A, B], points: results, string };
    }
    function _power(data) {
        const sum = [0, 0, 0, 0];
        let n = 0;
        const results = [];
        for (let len = data.length; n < len; n++) {
            if (data[n].x !== null) {
                data[n][0] = data[n].x;
                data[n][1] = data[n].y;
            }
            if (data[n][1] !== null) {
                sum[0] += Math.log(data[n][0]);
                sum[1] += Math.log(data[n][1]) * Math.log(data[n][0]);
                sum[2] += Math.log(data[n][1]);
                sum[3] += Math.pow(Math.log(data[n][0]), 2);
            }
        }
        const B = (n * sum[1] - sum[2] * sum[0]) / (n * sum[3] - sum[0] * sum[0]);
        const A = Math.pow(Math.E, (sum[2] - B * sum[0]) / n);
        for (let i = 0, len = data.length; i < len; i++) {
            const coordinate = [data[i][0], A * Math.pow(data[i][0], B)];
            results.push(coordinate);
        }
        results.sort(function (a, b) {
            if (a[0] > b[0]) {
                return 1;
            }
            if (a[0] < b[0]) {
                return -1;
            }
            return 0;
        });
        const string = `y = ${Math.round(A * 100) / 100}x^${Math.round(B * 100) / 100}`;
        return { equation: [A, B], points: results, string };
    }
    function gaussianElimination(a, o) {
        let i = 0;
        let j = 0;
        let k = 0;
        let maxrow = 0;
        let tmp = 0;
        const n = a.length - 1;
        const x = new Array(o);
        for (i = 0; i < n; i++) {
            maxrow = i;
            for (j = i + 1; j < n; j++) {
                if (Math.abs(a[i][j]) > Math.abs(a[i][maxrow]))
                    maxrow = j;
            }
            for (k = i; k < n + 1; k++) {
                tmp = a[k][i];
                a[k][i] = a[k][maxrow];
                a[k][maxrow] = tmp;
            }
            for (j = i + 1; j < n; j++) {
                for (k = n; k >= i; k--) {
                    a[k][j] -= (a[k][i] * a[i][j]) / a[i][i];
                }
            }
        }
        for (j = n - 1; j >= 0; j--) {
            tmp = 0;
            for (k = j + 1; k < n; k++)
                tmp += a[k][j] * x[k];
            x[j] = (a[n][j] - tmp) / a[j][j];
        }
        return x;
    }
    function _polynomial(data, order, extrapolate) {
        if (typeof order === 'undefined') {
            order = 2;
        }
        const lhs = [];
        const rhs = [];
        const results = [];
        let a = 0;
        let b = 0;
        let i = 0;
        const k = order + 1;
        for (; i < k; i++) {
            for (let l = 0, len = data.length; l < len; l++) {
                if (data[l].x !== null) {
                    data[l][0] = data[l].x;
                    data[l][1] = data[l].y;
                }
                if (data[l][1] !== null) {
                    a += Math.pow(data[l][0], i) * data[l][1];
                }
            }
            lhs.push(a);
            a = 0;
            const c = [];
            for (let j = 0; j < k; j++) {
                for (let l = 0, len = data.length; l < len; l++) {
                    if (data[l][1]) {
                        b += Math.pow(data[l][0], i + j);
                    }
                }
                c.push(b);
                b = 0;
            }
            rhs.push(c);
        }
        rhs.push(lhs);
        const equation = gaussianElimination(rhs, k);
        const resultLength = data.length + extrapolate;
        const step = data[data.length - 1][0] - data[data.length - 2][0];
        for (let i = 0, len = resultLength; i < len; i++) {
            let answer = 0;
            let x = 0;
            if (typeof data[i] !== 'undefined') {
                x = data[i][0];
            }
            else {
                x = data[data.length - 1][0] + (i - data.length) * step;
            }
            for (let w = 0; w < equation.length; w++) {
                answer += equation[w] * Math.pow(x, w);
            }
            results.push([x, answer]);
        }
        results.sort(function (a, b) {
            if (a[0] > b[0]) {
                return 1;
            }
            if (a[0] < b[0]) {
                return -1;
            }
            return 0;
        });
        let string = 'y = ';
        for (let i = equation.length - 1; i >= 0; i--) {
            if (i > 1)
                string += `${Math.round(equation[i] * 100) / 100}x^${i} + `;
            else if (i === 1)
                string += `${Math.round(equation[i] * 100) / 100}x + `;
            else
                string += Math.round(equation[i] * 100) / 100;
        }
        return { equation, points: results, string };
    }
    function _loess(data, bandwidth) {
        function array_unique(values) {
            const o = {};
            let i;
            const l = values.length;
            const r = [];
            for (i = 0; i < l; i += 1)
                o[values[i]] = values[i];
            for (i in o)
                r.push(o[i]);
            return r;
        }
        bandwidth = bandwidth || 0.25;
        const xval = data.map(function (pair) {
            return pair[0];
        });
        const distinctX = array_unique(xval);
        if (2 / distinctX.length > bandwidth) {
            bandwidth = Math.min(2 / distinctX.length, 1);
            console.warn(`updated bandwith to ${bandwidth}`);
        }
        const yval = data.map(function (pair) {
            return pair[1];
        });
        function tricube(x) {
            const tmp = 1 - x * x * x;
            return tmp * tmp * tmp;
        }
        const res = [];
        let left = 0;
        let right = Math.floor(bandwidth * xval.length) - 1;
        for (const idx in xval) {
            const x = xval[idx];
            const i = parseInt(idx, 10);
            if (i > 0) {
                if (right < xval.length - 1 &&
                    xval[right + 1] - xval[i] < xval[i] - xval[left]) {
                    left++;
                    right++;
                }
            }
            let edge;
            if (xval[i] - xval[left] > xval[right] - xval[i])
                edge = left;
            else
                edge = right;
            const denom = Math.abs(1.0 / (xval[edge] - x));
            let sumWeights = 0;
            let sumX = 0;
            let sumXSquared = 0;
            let sumY = 0;
            let sumXY = 0;
            let k = left;
            while (k <= right) {
                const xk = xval[k];
                const yk = yval[k];
                let dist;
                if (k < i) {
                    dist = x - xk;
                }
                else {
                    dist = xk - x;
                }
                const w = tricube(dist * denom);
                const xkw = xk * w;
                sumWeights += w;
                sumX += xkw;
                sumXSquared += xk * xkw;
                sumY += yk * w;
                sumXY += yk * xkw;
                k++;
            }
            const meanX = sumX / sumWeights;
            const meanY = sumY / sumWeights;
            const meanXY = sumXY / sumWeights;
            const meanXSquared = sumXSquared / sumWeights;
            let beta;
            if (meanXSquared === meanX * meanX)
                beta = 0;
            else
                beta =
                    (meanXY - meanX * meanY) / (meanXSquared - meanX * meanX);
            const alpha = meanY - beta * meanX;
            res[i] = beta * x + alpha;
        }
        return {
            equation: '',
            points: xval.map(function (x, i) {
                return [x, res[i]];
            }),
            string: '',
        };
    }
    function coefficientOfDetermination(data, pred) {
        data.sort(function (a, b) {
            if (a[0] > b[0]) {
                return 1;
            }
            if (a[0] < b[0]) {
                return -1;
            }
            return 0;
        });
        let mean = 0;
        let N = data.length;
        for (let i = 0; i < data.length; i++) {
            if (data[i][1] !== null) {
                mean += data[i][1];
            }
            else {
                N--;
            }
        }
        mean /= N;
        let SSE = 0;
        let SSYY = 0;
        for (let i = 0; i < data.length; i++) {
            if (data[i][1] !== null) {
                SSYY += Math.pow(data[i][1] - pred[i][1], 2);
                SSE += Math.pow(data[i][1] - mean, 2);
            }
        }
        return 1 - SSYY / SSE;
    }
    function standardError(data, pred) {
        let SE = 0;
        let N = data.length;
        for (let i = 0; i < data.length; i++) {
            if (data[i][1] !== null) {
                SE += Math.pow(data[i][1] - pred[i][1], 2);
            }
            else {
                N--;
            }
        }
        SE = Math.sqrt(SE / (N - 2));
        return SE;
    }
    H.wrap(H.Chart.prototype, 'init', function (proceed) {
        let series = arguments[1].series;
        const regressionLineExists = series.some(s => s.id === 'highcharts-regression');
        if (regressionLineExists) {
            series = series.filter(s => s.id !== 'highcharts-regression');
        }
        const extraSeries = [];
        let i = 0;
        for (i = 0; i < series.length; i++) {
            const s = series[i];
            if (s.regression) {
                s.regressionSettings = s.regressionSettings || {};
                s.regressionSettings.tooltip =
                    s.regressionSettings.tooltip || {};
                s.regressionSettings.dashStyle =
                    s.regressionSettings.dashStyle || 'solid';
                s.regressionSettings.decimalPlaces =
                    s.regressionSettings.decimalPlaces || 2;
                s.regressionSettings.useAllSeries =
                    s.regressionSettings.useAllSeries || false;
                const regressionType = s.regressionSettings.type || 'linear';
                let regression;
                const extraSerie = {
                    data: [],
                    color: s.regressionSettings.color || '',
                    yAxis: s.yAxis,
                    lineWidth: s.regressionSettings.lineWidth || 2,
                    marker: { enabled: false },
                    isRegressionLine: true,
                    visible: s.regressionSettings.visible,
                    type: s.regressionSettings.linetype || 'spline',
                    name: s.regressionSettings.name || 'Equation: %eq',
                    id: s.regressionSettings.id,
                    dashStyle: s.regressionSettings.dashStyle || 'solid',
                    showInLegend: !s.regressionSettings.hideInLegend,
                    tooltip: {
                        valueSuffix: s.regressionSettings.tooltip.valueSuffix || ' ',
                    },
                    dataLabels: s.regressionSettings.dataLabels || {},
                };
                let mergedData = s.data;
                if (s.regressionSettings.useAllSeries) {
                    mergedData = [];
                    for (let di = 0; di < series.length; di++) {
                        const seriesToMerge = series[di];
                        mergedData = mergedData.concat(seriesToMerge.data);
                    }
                }
                if (regressionType === 'linear') {
                    regression = _linear(mergedData);
                    extraSerie.type = 'line';
                }
                else if (regressionType === 'exponential') {
                    regression = _exponential(mergedData);
                }
                else if (regressionType === 'polynomial') {
                    const order = s.regressionSettings.order || 2;
                    const extrapolate = s.regressionSettings.extrapolate || 0;
                    regression = _polynomial(mergedData, order, extrapolate);
                }
                else if (regressionType === 'logarithmic') {
                    regression = _logarithmic(mergedData);
                }
                else if (regressionType === 'loess') {
                    const loessSmooth = s.regressionSettings.loessSmooth || 25;
                    regression = _loess(mergedData, loessSmooth / 100);
                }
                else {
                    throw new Error(`Invalid regression type: ${regressionType}`);
                }
                regression.rSquared = coefficientOfDetermination(mergedData, regression.points);
                regression.rValue = Math.sqrt(regression.rSquared).toFixed(s.regressionSettings.decimalPlaces);
                regression.rSquared = regression.rSquared.toFixed(s.regressionSettings.decimalPlaces);
                regression.standardError = standardError(mergedData, regression.points).toFixed(s.regressionSettings.decimalPlaces);
                extraSerie.data = regression.points;
                extraSerie.name = extraSerie.name.replace('%r2', regression.rSquared);
                extraSerie.name = extraSerie.name.replace('%r', regression.rValue);
                extraSerie.name = extraSerie.name.replace('%eq', regression.string);
                extraSerie.name = extraSerie.name.replace('%se', regression.standardError);
                extraSerie.className = 'highcharts-regression';
                extraSerie.id = extraSerie.className;
                extraSerie.blinkSeriesId = extraSerie.className;
                extraSerie.valueColumnIdentifier = s.valueColumnIdentifier;
                if (extraSerie.visible === false) {
                    extraSerie.visible = false;
                }
                extraSerie.regressionOutputs = regression;
                extraSeries.push(extraSerie);
            }
        }
        arguments[1].series = series.concat(extraSeries);
        proceed.apply(this, Array.prototype.slice.call(arguments, 1));
    });
}
//# sourceMappingURL=highcharts-regression.js.map